import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import useCurrentUser from "../hooks/useCurrentUser";
import {
  PackagePlus, ClipboardCheck, ShieldCheck, Truck, Bell, FileText,
  ChevronRight, Clock, CheckCircle2, AlertCircle, Loader2
} from "lucide-react";

const STAGES = [
  { id: "created",   label: "Shipment Created",     icon: PackagePlus,    color: "bg-blue-500",    light: "bg-blue-50 border-blue-200 text-blue-700" },
  { id: "reviewing", label: "Operator Review",       icon: ClipboardCheck, color: "bg-orange-500",  light: "bg-orange-50 border-orange-200 text-orange-700" },
  { id: "customs",   label: "Customs / Validation",  icon: ShieldCheck,    color: "bg-purple-500",  light: "bg-purple-50 border-purple-200 text-purple-700" },
  { id: "in_transit",label: "In Transit",            icon: Truck,          color: "bg-teal-500",    light: "bg-teal-50 border-teal-200 text-teal-700" },
  { id: "delivered", label: "Delivered",             icon: Bell,           color: "bg-emerald-500", light: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  { id: "completed", label: "Docs & Invoices Stored",icon: FileText,       color: "bg-slate-500",   light: "bg-slate-50 border-slate-200 text-slate-700" },
];

// Map raw Firestore statuses → pipeline stage IDs
function mapStatus(status = "") {
  const s = status.toLowerCase();
  if (s.includes("creat") || s === "draft" || s === "pending") return "created";
  if (s.includes("review") || s.includes("request") || s.includes("approved")) return "reviewing";
  if (s.includes("custom") || s.includes("clearance") || s.includes("hold")) return "customs";
  if (s.includes("transit") || s.includes("shipped") || s.includes("loading") || s.includes("loaded")) return "in_transit";
  if (s.includes("deliver") || s.includes("arrived")) return "delivered";
  if (s.includes("complet") || s.includes("invoic") || s.includes("closed")) return "completed";
  return "created";
}

function ShipmentCard({ shipment, stageColor }) {
  return (
    <div className={`p-3 rounded-xl border ${stageColor} mb-2 last:mb-0`}>
      <div className="flex justify-between items-start gap-2">
        <div>
          <div className="font-bold text-sm truncate max-w-[150px]">
            {shipment.referenceNumber || shipment.reference || shipment.id?.slice(0, 8)}
          </div>
          {(shipment.originPort || shipment.origin) && (
            <div className="text-[11px] opacity-70 mt-0.5">
              {shipment.originPort || shipment.origin} → {shipment.destinationPort || shipment.destination || "TBD"}
            </div>
          )}
        </div>
        {shipment.eta && (
          <div className="flex items-center gap-1 text-[10px] opacity-60 shrink-0">
            <Clock size={10} />
            {new Date(shipment.eta?.toDate?.() || shipment.eta).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OperationalFlow({ collectionPath, companyIdFilter }) {
  const { clientId, role } = useCurrentUser();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  const effectiveCompanyId = companyIdFilter || clientId;

  useEffect(() => {
    if (!effectiveCompanyId && role !== "operator" && role !== "super-admin" && role !== "admin") {
      setLoading(false);
      return;
    }

    const col = collectionPath || "shipments";
    let q;

    if (effectiveCompanyId) {
      q = query(collection(db, col), where("companyId", "==", effectiveCompanyId), orderBy("createdAt", "desc"), limit(50));
    } else {
      q = query(collection(db, col), orderBy("createdAt", "desc"), limit(50));
    }

    const unsub = onSnapshot(q, (snap) => {
      setShipments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return () => unsub();
  }, [effectiveCompanyId, collectionPath, role]);

  // Group shipments by pipeline stage
  const grouped = STAGES.reduce((acc, s) => ({ ...acc, [s.id]: [] }), {});
  shipments.forEach(s => {
    const stage = mapStatus(s.status);
    grouped[stage].push(s);
  });

  const total = shipments.length;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-navy-900 font-serif">Operational Flow</h2>
          <p className="text-slate-400 text-sm mt-1">Live shipment pipeline — {total} active shipment{total !== 1 ? "s" : ""}</p>
        </div>
        {loading && <Loader2 size={20} className="animate-spin text-slate-400" />}
      </div>

      {/* Pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const items = grouped[stage.id] || [];
          return (
            <div key={stage.id} className="flex flex-col">
              {/* Stage Header */}
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded-lg ${stage.color} flex items-center justify-center shrink-0`}>
                  <Icon size={14} className="text-white" />
                </div>
                <div>
                  <div className="text-xs font-bold text-navy-900 leading-tight">{stage.label}</div>
                  <div className="text-[10px] text-slate-400">{items.length} shipment{items.length !== 1 ? "s" : ""}</div>
                </div>
                {idx < STAGES.length - 1 && (
                  <ChevronRight size={14} className="text-slate-300 hidden xl:block ml-auto" />
                )}
              </div>

              {/* Cards */}
              <div className="bg-slate-100/60 rounded-2xl p-2 flex-1 min-h-[120px]">
                {items.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-[11px] text-slate-300 font-medium text-center p-2">
                    No shipments
                  </div>
                ) : (
                  items.slice(0, 5).map(s => (
                    <ShipmentCard key={s.id} shipment={s} stageColor={stage.light} />
                  ))
                )}
                {items.length > 5 && (
                  <div className="text-center text-[10px] text-slate-400 font-bold mt-1">
                    +{items.length - 5} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary bar */}
      {total > 0 && (
        <div className="mt-6 flex flex-wrap gap-3">
          {STAGES.map(s => {
            const count = grouped[s.id]?.length || 0;
            if (!count) return null;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={s.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${s.light}`}>
                <div className={`w-2 h-2 rounded-full ${s.color}`} />
                {s.label}: {count} ({pct}%)
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
